/**
 * CsDemoSharePanel — Iter-112 · Item 4. Prospect Demo Sharing.
 * CS generates secure expiring links (24h/48h/72h/7d) per Cleaners demo
 * account, can revoke early, and sees generation/access events logged.
 */
import { useEffect, useState } from 'react';
import { Share2, Copy, Ban, Clock3 } from 'lucide-react';
import { Card } from '../ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const EXPIRY_OPTIONS = [
  { hours: 24, label: '24 hours' },
  { hours: 48, label: '48 hours' },
  { hours: 72, label: '72 hours (default)' },
  { hours: 168, label: '7 days' },
];

export const CsDemoSharePanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [links, setLinks] = useState([]);
  const [expiry, setExpiry] = useState({}); // per-type expiry choice
  const [denied, setDenied] = useState(false);

  const load = () => {
    fetch(`${API}/api/cleaners/demo-accounts`, { credentials: 'include' })
      .then((r) => { if (r.status === 403) { setDenied(true); return null; } return r.ok ? r.json() : null; })
      .then((d) => { if (d) setAccounts(d.accounts || []); })
      .catch(() => {});
    fetch(`${API}/api/cleaners/demo-accounts/shares/all`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setLinks(d.links || []); })
      .catch(() => {});
  };
  useEffect(load, []);

  const generate = async (ptype) => {
    try {
      const r = await fetch(`${API}/api/cleaners/demo-accounts/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ property_type: ptype, expiry_hours: Number(expiry[ptype] || 72) }),
      });
      if (!r.ok) throw new Error('Could not generate link');
      const d = await r.json();
      const url = `${window.location.origin}${d.share_path}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success('Share link generated & copied to clipboard');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const revoke = async (token) => {
    await fetch(`${API}/api/cleaners/demo-accounts/shares/${token}/revoke`, { method: 'POST', credentials: 'include' }).catch(() => {});
    toast.success('Link revoked');
    load();
  };

  const copyLink = (token) => {
    navigator.clipboard?.writeText(`${window.location.origin}/demo-share/${token}`)
      .then(() => toast.success('Link copied'));
  };

  if (denied || accounts.length === 0) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="cs-demo-share-panel">
      <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: '#22d3ee' }}>
        <Share2 className="h-3.5 w-3.5 inline mr-1.5" />
        Prospect Demo Sharing · Cleaners Commercial Demos
      </p>
      <p className="text-[11px] text-zinc-500 mb-4">
        Generate a secure expiring link per demo — prospects open it with no login. Revoke anytime below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
        {accounts.map((a) => (
          <div key={a.property_type} className="rounded-lg p-3" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${a.accent || '#22d3ee'}55` }} data-testid={`cs-share-tile-${a.property_type}`}>
            <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: a.accent || '#22d3ee' }}>{a.necs_name}</p>
            <p className="text-xs font-bold text-white truncate mb-2">{a.property_name}</p>
            <div className="flex items-center gap-1.5">
              <select value={expiry[a.property_type] || 72}
                      onChange={(e) => setExpiry((x) => ({ ...x, [a.property_type]: e.target.value }))}
                      className="text-[10px] px-1.5 py-1 rounded bg-zinc-900 border border-zinc-700 text-white flex-1"
                      data-testid={`cs-share-expiry-${a.property_type}`}>
                {EXPIRY_OPTIONS.map((o) => <option key={o.hours} value={o.hours}>{o.label}</option>)}
              </select>
              <button type="button" onClick={() => generate(a.property_type)}
                      className="text-[10px] uppercase font-black px-2.5 py-1.5 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: a.accent || '#22d3ee', color: '#0a0a0a' }}
                      data-testid={`cs-share-generate-${a.property_type}`}>
                Share Demo
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#C9A962' }}>
        Active & Recent Links
      </p>
      {links.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="cs-share-links-empty">No links generated yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="cs-share-links-list">
          {links.map((l) => (
            <div key={l.token} className="flex items-center gap-2 rounded px-3 py-2 text-xs flex-wrap" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }} data-testid={`cs-share-link-${l.token}`}>
              <span className="font-bold text-white">{l.necs_name}</span>
              <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full"
                    style={l.status === 'active'
                      ? { color: '#34d399', border: '1px solid #34d399' }
                      : { color: '#f87171', border: '1px solid #f87171' }}
                    data-testid={`cs-share-status-${l.token}`}>
                {l.status}
              </span>
              <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                <Clock3 className="h-3 w-3" /> expires {new Date(l.expires_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="text-[10px] text-zinc-500">· {l.access_count || 0} open{(l.access_count || 0) !== 1 ? 's' : ''} · {(l.events || []).length} events · by {l.created_by}</span>
              <span className="ml-auto flex items-center gap-1.5">
                <button type="button" onClick={() => copyLink(l.token)} className="text-[10px] font-bold px-2 py-1 rounded" style={{ border: '1px solid #22d3ee', color: '#22d3ee' }} data-testid={`cs-share-copy-${l.token}`}>
                  <Copy className="h-3 w-3 inline" /> Copy
                </button>
                {l.status === 'active' && (
                  <button type="button" onClick={() => revoke(l.token)} className="text-[10px] font-bold px-2 py-1 rounded" style={{ border: '1px solid #ef4444', color: '#ef4444' }} data-testid={`cs-share-revoke-${l.token}`}>
                    <Ban className="h-3 w-3 inline" /> Revoke
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default CsDemoSharePanel;
